import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Button,
    Typography,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useEffect, useState } from 'react';
import {useMutation, useQuery} from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';

import {
    ALL_CONTENT_INFOS,
    CREATE_CONTENT_INFO,
    ALL_SERVICE_TYPES,
    UPDATE_CONTENT_INFO,
    ALL_CONTENT_TYPES
} from '../queries/GraphQLQueries';
import FileLink from "./FileLink";
import CustomAlert from "./CustomAlert";
import OneDriveFilePicker from './OneDriveFilePicker';
import CapLevelSelect from "./CapLevelSelect";
import ServiceTypeSelect from "./ServiceTypeSelect";
import ContentTypeSelect from './ContentTypeSelect';
import {getContentCapLevelKey, getDefaultCapLevel} from "../helpers/CapLevelHelper";
import {useDialogNavigation} from "../hooks/useDialogNavigation";

const CODE_MAX_CHAR_ALLOWED = 20;
const MAX_CHAR_ALLOWED = 100;

export default function CreateUpdateContentDialogBox() {

    const { state } = useLocation();
    const { contentId, contentDescription, contentName, contentNameExternal, contentCode, contentIsAssessed, contentExternalUrl, contentExternalUrlName, currentServiceTypeKeycode, currentServiceType, contentLevel, currentContentType, contentFolderId, isEditDialogBox } = state || {};
    const [capLevel, setCapLevel] = useState(contentLevel ? getContentCapLevelKey(contentLevel) : getDefaultCapLevel());
    const [contentType, setContentType] = useState(currentContentType ? currentContentType : "");
    const [serviceTypeKeycode, setServiceTypeKeycode] = useState(currentServiceTypeKeycode ? currentServiceTypeKeycode : "");
    const [isServiceTypeCapSegmented, setIsServiceTypeCapSegmented] = useState(true);
    const [allServiceTypes, setAllServiceTypes] = useState([]);
    const [allContentTypes, setAllContentTypes] = useState([]);

    const navigate = useNavigate();
    const handleCancel = useDialogNavigation();
    const [updateContentMutation] = useMutation(UPDATE_CONTENT_INFO, {
        refetchQueries: [
            { query: ALL_CONTENT_INFOS }, 'GetContentInfos'
        ]
    });
    const [createContentMutation] = useMutation(CREATE_CONTENT_INFO, {
        refetchQueries: [
            { query: ALL_CONTENT_INFOS }, 'GetContentInfos'
        ]
    });
    const { loading, error, data } = useQuery(ALL_SERVICE_TYPES);
    const { loading:contentTypeLoading, errors:contentTypeErrors, data: contentTypeData } = useQuery(ALL_CONTENT_TYPES);

    const [name, setName] = useState(contentName || "");
    const [nameExternal, setNameExternal] = useState(contentNameExternal || "");
    const [code, setCode] = useState(contentCode || "");
    const [externalUrl, setExternalUrl] = useState(contentExternalUrl);
    const [externalFileName, setExternalFileName] = useState(contentExternalUrlName);
    const [description, setDescription] = useState(contentDescription || "");
    const [isAssessed, setIsAssessed] = useState(contentIsAssessed || false);
    const [canSubmit, setCanSubmit] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const handleCloseCustomAlert = () => {
        setAlertMessage(null);
    };
    const [filePickerButtonLabel] = useState('Select File');

    const [dialogBoxTitle] = useState(() => {
        return isEditDialogBox ? 'Edit Content Definition' : 'Create Content Definition';
    });

    useEffect(() => {

        const canEnableSubmit = isStringNonEmpty(name)
            && isStringNonEmpty(serviceTypeKeycode)
            && isStringNonEmpty(externalUrl)
            && isStringNonEmpty(capLevel)
            && (description !== contentDescription
                || name !== contentName
                || isStringNonEmptyAndMatching(nameExternal, contentNameExternal)
                || isStringNonEmptyAndMatching(code, contentCode)
                || externalUrl !== contentExternalUrl
                || externalFileName !== contentExternalUrlName
                || isAssessed !== contentIsAssessed
                || contentType !== currentContentType
                || capLevel !== getContentCapLevelKey(contentLevel)
                || (!isServiceTypeCapSegmented && capLevel === getDefaultCapLevel()));

        setCanSubmit(canEnableSubmit);
    }, [name, nameExternal, code, description, isAssessed, externalUrl, capLevel, contentType, serviceTypeKeycode, isServiceTypeCapSegmented]);

    useEffect(() => {
        if (data?.serviceTypes) {
            setAllServiceTypes(data.serviceTypes);

            if (!serviceTypeKeycode) {
                const defaultServiceTypes = data.serviceTypes.filter(type => type.isDefault);

                if (defaultServiceTypes?.length === 1) {
                    const { keycode, isCapSegmented } = defaultServiceTypes[0];

                    setServiceTypeKeycode(keycode);
                    setIsServiceTypeCapSegmented(isCapSegmented);
                }
            }
        }
    }, [data, allServiceTypes]);

    useEffect(() => {
        setCapLevel(contentLevel ? getContentCapLevelKey(contentLevel) : getDefaultCapLevel());
    }, [isServiceTypeCapSegmented]);

    useEffect(() => {
        if (contentTypeData?.contentTypes) {
            setAllContentTypes(contentTypeData.contentTypes)
            if (!currentContentType) {
                setContentType(contentTypeData.contentTypes[0]);
            }
        }
    }, [contentTypeData]);

    function handleSubmit() {

        setCanSubmit(false);

        if (isEditDialogBox) {
            setAlertMessage({ children: 'Updating content...', severity: 'success' });
            executeUpdateContentMutation();
        } else {
            setAlertMessage({ children: 'Creating content...', severity: 'success' });
            executeCreateContentMutation();
        }
    }

    function executeUpdateContentMutation() {
        updateContentMutation({
            variables: {
                id: contentId,
                name: name,
                nameExternal: nameExternal,
                code: code,
                contentType: contentType,
                description: description,
                serviceTypeKeycode: serviceTypeKeycode,
                isAssessed: isAssessed,
                externalUrl: externalUrl,
                externalUrlName: externalFileName,
                capLevel: capLevel
            }
        }).then((data) => {
            navigate('..', {state: { data }});
        }).catch((e) => {
            setAlertMessage({ children: e?.message, severity: 'error' });
            setCanSubmit(true);
        });
    }

    function executeCreateContentMutation() {
        createContentMutation({
            variables: {
                name: name,
                nameExternal: nameExternal,
                code: code,
                description: description,
                serviceTypeKeycode: serviceTypeKeycode,
                theCapLevel: capLevel,
                contentType: contentType,
                isAssessed: isAssessed,
                externalUrl: externalUrl,
                externalUrlName: externalFileName,
                contentFolderId: contentFolderId,
            }
        }).then(() => {
            handleCancel();
        }).catch((e) => {
            setAlertMessage({ children: e?.message, severity: 'error' });
            setCanSubmit(true);
        });
    }

    function handleChosenFile(fileData) {

        if (fileData?.errorCode) {
            setAlertMessage({ children: 'Error picking file.', severity: 'error' });
            return;
        }

        const contentWebUrl = fileData.value[0].webUrl;

        setExternalUrl(contentWebUrl);
        setExternalFileName(fileData.value[0].name);
    }

    function handleCapLevelChanged(value) {
        setCapLevel(value);
    }

    function handleContentTypeChange(value) {
        setContentType(value);
    }

    function handleServiceTypeUpdate(value) {
        setServiceTypeKeycode(value);

        const selectedServiceTypes = allServiceTypes.filter(type => type.keycode === value);

        if (selectedServiceTypes?.length === 1) {
            setIsServiceTypeCapSegmented(selectedServiceTypes[0].isCapSegmented);
        }
    }

    function isStringNonEmpty(value) {
        return !!value && value.trim() !== '';
    }

    function isStringNonEmptyAndMatching(value, newValue) {
        return (isStringNonEmpty(value) || isStringNonEmpty(newValue)) && value !== newValue;
    }

    return <>
        <Dialog open={true} onClose={handleCancel} disableRestoreFocus>
            <DialogTitle>{dialogBoxTitle}</DialogTitle>
            <DialogContent>
                <Grid container spacing={3}>
                    <Grid size={6}>
                        <TextField id='content-code'
                            label={'Code'}
                            variant='standard'
                            autoComplete='off'
                            value={code}
                            helperText={`${code?.length}/${CODE_MAX_CHAR_ALLOWED}`}       
                            onChange={(e) => setCode(e.target.value)}
                            slotProps={{input:
                                {inputProps:
                                    {maxLength: CODE_MAX_CHAR_ALLOWED}
                                }
                            }}
                        />
                    </Grid>
                    <Grid size={12}>
                        <TextField id='content-name'
                            label={'Name'}
                            variant='standard'
                            required
                            autoComplete='off'
                            value={name} 
                            helperText={`${name?.length}/${MAX_CHAR_ALLOWED}`}
                            onChange={(e) => setName(e.target.value)}
                            slotProps={{input:
                                {inputProps:
                                    {maxLength: MAX_CHAR_ALLOWED}
                                }
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={12}>
                        <TextField id='content-name-external'
                            label={'External Name'}
                            variant='standard'
                            autoComplete='off'
                            value={nameExternal}
                            helperText={`${nameExternal?.length}/${MAX_CHAR_ALLOWED}`}
                            onChange={(e) => setNameExternal(e.target.value)}
                            slotProps={{input:
                                {inputProps:
                                    {maxLength: MAX_CHAR_ALLOWED}
                                }
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={12}>
                        <TextField
                            id='content-description'
                            label='Description'
                            variant='standard'
                            autoComplete='off'
                            value={description}
                            helperText={`${description?.length}/${MAX_CHAR_ALLOWED}`}
                            onChange={(e) => setDescription(e.target.value)}
                            slotProps={{input:
                                {inputProps:
                                    {maxLength: MAX_CHAR_ALLOWED}
                                }
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={6}>
                        <ServiceTypeSelect
                            isNonEditable={isEditDialogBox}
                            handleServiceTypeUpdate={handleServiceTypeUpdate}
                            currentServiceType={currentServiceType}
                            currentServiceTypeKeycode={serviceTypeKeycode}
                            availableServiceTypes={allServiceTypes}
                        />
                    </Grid>
                    <Grid size={6}>
                    </Grid>
                    <Grid size={6}>
                        <CapLevelSelect
                            isServiceTypeCapSegmented={isServiceTypeCapSegmented}
                            currentCapLevel={capLevel}
                            handleCapLevelChanged={handleCapLevelChanged}
                        />
                    </Grid>
                    <Grid size={6}>
                        <ContentTypeSelect
                            handleContentTypeChange={handleContentTypeChange}
                            currentContentType={contentType}
                            allContentTypes={allContentTypes}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Typography
                            variant="caption"
                            style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                            Content File *
                        </Typography>
                        <FileLink fileName={externalFileName} fileUrl={externalUrl} />
                    </Grid>
                    <Grid size={6}>
                        <OneDriveFilePicker filePickerButtonLabel={filePickerButtonLabel} handleChosenFile={handleChosenFile} />
                    </Grid>
                    <Grid size={6}>
                        <FormControlLabel 
                            control={<Checkbox checked={isAssessed} onChange={(e) => setIsAssessed(e.target.checked)} />} 
                            label="Is Assessed" />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} variant="contained">Cancel</Button>
                <Button onClick={handleSubmit} disabled={!canSubmit} variant="contained">Submit</Button>
            </DialogActions>
        </Dialog>
        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
    </>;
}
